/**
 * ActionsMenu Component
 * 
 * A dropdown menu with three dots (⋮) that contains:
 * - Usage card (inline subscription info)
 * - Rename agent
 * - New agent
 * - Delete agent
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import UsageCard from './UsageCard';

interface ActionsMenuProps {
  /** Callback for rename action */
  onRename: () => void;
  /** Callback for new agent action */
  onNewAgent: () => void;
  /** Callback for delete action */
  onDelete: () => void;
  /** Callback for upgrade action - opens AgentUpgradeModal */
  onUpgrade: () => void;
  /** Whether there is at least one agent available (controls visibility of rename/delete) */
  hasAgents?: boolean;
  /** Whether new agent action is disabled */
  isCreatingAgent?: boolean;
}

const ActionsMenu = ({ onRename, onNewAgent, onDelete, onUpgrade, hasAgents = true, isCreatingAgent }: ActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Keep existing behavior: open down + align end (expand to the left)
    setPosition({
      top: rect.bottom + 8,
      left: rect.right - 280,
    });
  }, []);

  // Position before paint to avoid flashing at (0,0)
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [isOpen, updatePosition]);

  // Reposition while open (scroll/resize) to stay anchored reliably
  useEffect(() => {
    if (!isOpen) return;
    let raf = 0;
    const onReposition = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updatePosition);
    };
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updatePosition]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        menuRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleAction = useCallback((action: () => void) => {
    setIsOpen(false);
    action();
  }, []);

  return (
    <div className="relative inline-flex">
      {/* Three dots button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-center w-10 h-10 rounded-lg border transition-all
          ${isOpen
            ? 'bg-white/10 border-white/20'
            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/15'}
        `}
        title="More actions"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white/70">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>

      {/* Dropdown menu rendered via portal to guarantee it's above chat/stacking contexts */}
      {isOpen && position && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999]"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
          role="menu"
        >
          <div className="w-[280px] bg-[#1a2332] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
            {/* Usage Card - inline subscription info */}
            <UsageCard isActive={isOpen} onUpgrade={() => { setIsOpen(false); onUpgrade(); }} />

            {hasAgents && (
              <>
                {/* Divider */}
                <div className="h-px bg-white/5 mx-2" />

                {/* Rename */}
                <button
                  onClick={() => handleAction(onRename)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                  type="button"
                  role="menuitem"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/60">
                    <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Rename</span>
                </button>
              </>
            )}

            {/* New Agent */}
            <button
              onClick={() => handleAction(onNewAgent)}
              disabled={isCreatingAgent}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              role="menuitem"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/60">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>New Agent</span>
            </button>

            {hasAgents && (
              <>
                {/* Divider */}
                <div className="h-px bg-white/5 mx-2" />

                {/* Delete - with red accent */}
                <button
                  onClick={() => handleAction(onDelete)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                  type="button"
                  role="menuitem"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Delete</span>
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionsMenu;
